export interface CityData {
  name: string;
  lat: number;
  lng: number;
}

export interface LocationData {
  slug: string;
  name: string;
  state: string;
  region: string;
  description: string;
  highlights: string[];
  content: string[];
  isPrimary: boolean;
  lat: number;
  lng: number;
  cities: CityData[];
}

export const locations: LocationData[] = [
  {
    slug: "ontario",
    name: "Ontario",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: true,
    lat: 34.0633,
    lng: -117.6509,
    description:
      "EMD Cleaning Service is headquartered at 500 East E Street in Ontario, California. We provide deep cleaning, regular cleaning, post-construction cleaning, and junk removal for homes and businesses across the Inland Empire, Monday through Saturday.",
    highlights: [
      "Headquartered in Ontario, CA",
      "Monday–Saturday, 8 AM – 6 PM",
      "Deep, regular & post-construction cleaning",
      "20% off for returning customers",
    ],
    content: [
      "Ontario is where EMD Cleaning Service started and where our crews are based. Our office sits on East E Street just a few minutes from Euclid Avenue and the Downtown Ontario corridor, so we know the difference between a 1920s bungalow near Euclid and a brand-new build out in Ontario Ranch. Both get the same detailed, room-by-room attention.",
      "The city never really slows down, and neither do the messes. We clean warehouses and offices near Ontario International Airport, retail suites around Ontario Mills, restaurants along Fourth Street, and hundreds of homes tucked between the 10 and the 60. Whether you need a one-time deep clean, a standing weekly visit, or a full post-construction turnover before you hand over the keys, we bring our own supplies and a checklist matched to your space.",
      "Ontario neighbors get first pick of our schedule, and returning customers save 20% on every visit. Call (909) 443-9863 for a free estimate in Ontario today.",
    ],
    cities: [
      { name: "Ontario Mills", lat: 34.0736, lng: -117.5545 },
      { name: "Ontario Ranch", lat: 33.9905, lng: -117.625 },
      { name: "Downtown Ontario", lat: 34.0639, lng: -117.6484 },
      { name: "Airport District", lat: 34.0559, lng: -117.6011 },
    ],
  },
  {
    slug: "chino",
    name: "Chino",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0122,
    lng: -117.6889,
    description:
      "House cleaning and commercial cleaning in Chino, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, specific area cleaning, and junk removal throughout the Chino Valley.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "Chino still has one foot in its agricultural past and one in its future. We clean older ranch homes along Central Avenue, tract houses near Chino High, and the newer three-story builds going up in The Preserve south of Pine Avenue. Dairy-country dust is real, and our deep cleans are built to handle it.",
      "On the commercial side, we serve the light-industrial parks near Chino Airport, medical suites off Riverside Drive, and small storefronts in the historic downtown. Post-construction cleanup is a regular request here — with so much new development south of the 60, contractors call us to strip drywall dust, wipe down fixtures, and get a unit photo-ready.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Chino cleaning.",
    ],
    cities: [
      { name: "The Preserve", lat: 33.97, lng: -117.665 },
      { name: "Downtown Chino", lat: 34.0122, lng: -117.6889 },
      { name: "Chino Airport Area", lat: 33.9747, lng: -117.637 },
      { name: "Los Serranos", lat: 33.97, lng: -117.71 },
    ],
  },
  {
    slug: "chino-hills",
    name: "Chino Hills",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 33.9898,
    lng: -117.7326,
    description:
      "Trusted residential cleaning in Chino Hills, CA. From hillside estates to family homes, EMD Cleaning Service delivers deep cleaning, regular cleaning, and specific area cleaning with a careful, detail-first crew.",
    highlights: [
      "Large-home and estate cleaning",
      "Weekly, bi-weekly & monthly service",
      "Specific area cleaning available",
      "Insured, uniformed cleaners",
    ],
    content: [
      "Chino Hills homes tend to be big, layered, and full of the kind of surfaces that show every fingerprint — tall entryways, wood floors, stone counters, plenty of glass facing the hills. Our crews size the team to the house so a 4,000-square-foot property in Vellano doesn't get the same one-person treatment as a condo off Grand Avenue.",
      "We work throughout the city: the Los Serranos side near the golf course, the quieter pockets along Carbon Canyon Road toward Sleepy Hollow, and the newer developments overlooking Chino Hills State Park. Clients here often start with a single deep clean, then move to a recurring schedule once they see the difference in the baseboards, blinds, and grout.",
      "If you only need certain rooms handled — kitchen and bathrooms, say, or a garage cleared out — our specific area cleaning and junk removal cover it. Reach EMD Cleaning Service at (909) 443-9863 for a free Chino Hills quote.",
    ],
    cities: [
      { name: "Los Serranos", lat: 33.97, lng: -117.71 },
      { name: "Vellano", lat: 33.952, lng: -117.742 },
      { name: "Sleepy Hollow", lat: 33.92, lng: -117.783 },
      { name: "Carbon Canyon", lat: 33.927, lng: -117.722 },
    ],
  },
  {
    slug: "pomona",
    name: "Pomona",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0551,
    lng: -117.75,
    description:
      "Cleaning services in Pomona, CA for homes, apartments, offices, and storefronts. EMD Cleaning Service offers deep cleaning, regular cleaning, post-construction cleaning, and junk removal at honest, up-front prices.",
    highlights: [
      "Residential & commercial cleaning",
      "Apartment and rental turnovers",
      "Post-construction cleanup",
      "20% returning customer discount",
    ],
    content: [
      "Pomona is a city of contrasts, and our work here reflects that. One morning we're deep cleaning a Craftsman near Lincoln Park, the afternoon is a student rental turnover close to Cal Poly Pomona, and the next day it's an office suite off Garey Avenue. The housing stock is old enough that detail matters — original tile, wood trim, and windows that reward a proper wash.",
      "The Downtown Pomona arts district and the Fairplex keep foot traffic high, and the businesses around them need spaces that stay presentable. We handle recurring janitorial-style maintenance cleaning for small offices, salons, studios, and shops, scheduling around business hours whenever we can.",
      "Landlords and property managers in Phillips Ranch and Ganesha Hills use us for move-out cleans paired with junk removal, so a unit is listing-ready in one visit. Call (909) 443-9863 to book EMD Cleaning Service in Pomona.",
    ],
    cities: [
      { name: "Phillips Ranch", lat: 34.025, lng: -117.79 },
      { name: "Downtown Pomona", lat: 34.0553, lng: -117.7513 },
      { name: "Ganesha Hills", lat: 34.07, lng: -117.77 },
      { name: "Lincoln Park", lat: 34.064, lng: -117.742 },
    ],
  },
  {
    slug: "west-covina",
    name: "West Covina",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0686,
    lng: -117.939,
    description:
      "West Covina, CA cleaning company serving homes and businesses along the 10 and 605 corridors. Deep cleaning, regular cleaning, maintenance cleaning, and junk removal from EMD Cleaning Service.",
    highlights: [
      "Serving all of the San Gabriel Valley east end",
      "Flexible weekday and Saturday slots",
      "One-time deep cleans available",
      "Own supplies and equipment provided",
    ],
    content: [
      "West Covina sits right where the 10 meets the 605, which makes it an easy stop for our crews heading west out of Ontario. We clean throughout the city — the hillside homes above South Hills, the established neighborhoods around Woodside Village, and the condos and townhomes closer to the Eastland Center shopping district.",
      "A lot of our West Covina work is recurring. Families with two working parents book bi-weekly regular cleaning; retirees often prefer monthly maintenance cleaning that keeps things from ever getting away from them. Either way you get consistent crews, a written checklist, and no surprise charges.",
      "Bigger jobs are welcome too: whole-house deep cleans before a party, post-remodel cleanup after a kitchen redo, or hauling away everything a garage has collected. Call EMD Cleaning Service at (909) 443-9863 for a free West Covina estimate.",
    ],
    cities: [
      { name: "Eastland Center", lat: 34.073, lng: -117.905 },
      { name: "South Hills", lat: 34.043, lng: -117.928 },
      { name: "Woodside Village", lat: 34.035, lng: -117.893 },
      { name: "Vincent", lat: 34.077, lng: -117.925 },
    ],
  },
  {
    slug: "montclair",
    name: "Montclair",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0775,
    lng: -117.6897,
    description:
      "Affordable, thorough cleaning in Montclair, CA. EMD Cleaning Service is minutes away and offers deep cleaning, regular house cleaning, specific area cleaning, and junk removal six days a week.",
    highlights: [
      "Just minutes from our Ontario office",
      "Same-week scheduling most weeks",
      "Apartment & condo cleaning",
      "Junk removal add-on available",
    ],
    content: [
      "Montclair is practically next door to us, which means short drives, punctual arrivals, and the ability to fit in last-minute requests other companies turn down. We work throughout the city, from the apartment complexes near Montclair Place to the tidy postwar homes north of Moreno Street.",
      "The mix here leans residential, and so does our work: kitchens degreased, bathrooms scrubbed to the grout, floors done properly rather than pushed around. Renters call us for move-out cleans that actually get the deposit back, and homeowners book recurring visits that keep the whole house even.",
      "We also take on the small commercial jobs around Central Avenue and Holt Boulevard — offices, salons, and shops that need a clean before opening or after close. Call (909) 443-9863 and we'll get Montclair on the calendar.",
    ],
    cities: [
      { name: "Montclair Place", lat: 34.073, lng: -117.696 },
      { name: "North Montclair", lat: 34.093, lng: -117.69 },
      { name: "Monte Vista", lat: 34.08, lng: -117.68 },
    ],
  },
  {
    slug: "claremont",
    name: "Claremont",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0967,
    lng: -117.7198,
    description:
      "Careful, respectful cleaning in Claremont, CA. EMD Cleaning Service works on historic homes, faculty residences, and student rentals near the Claremont Colleges with deep cleaning, regular cleaning, and move-out service.",
    highlights: [
      "Experience with older and historic homes",
      "Student rental turnovers",
      "Regular & maintenance cleaning plans",
      "Background-checked, insured team",
    ],
    content: [
      "Claremont's tree-lined streets hide a lot of beautiful, aging houses — Craftsman woodwork, original hardwood, plaster walls, and tile that needs the right touch rather than the strongest chemical. Our crews adjust products and methods to the surface, which matters more here than almost anywhere else we work.",
      "The Claremont Colleges shape our calendar. Every May and August we handle a wave of student rental turnovers near College Avenue and Foothill Boulevard: deep cleans, appliance interiors, and junk removal for whatever got left behind. Faculty and staff households in the Village and up toward Padua Hills tend to book recurring service instead.",
      "If you're hosting, selling, or just tired of the to-do list, one deep clean resets everything and a maintenance plan keeps it there. Call EMD Cleaning Service at (909) 443-9863 for a free Claremont estimate.",
    ],
    cities: [
      { name: "Claremont Village", lat: 34.096, lng: -117.719 },
      { name: "Claremont Colleges", lat: 34.103, lng: -117.708 },
      { name: "Padua Hills", lat: 34.142, lng: -117.712 },
      { name: "Pilgrim Place", lat: 34.099, lng: -117.725 },
    ],
  },
  {
    slug: "upland",
    name: "Upland",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0975,
    lng: -117.6484,
    description:
      "Upland, CA house cleaning and office cleaning. EMD Cleaning Service provides deep cleaning, regular cleaning, post-construction cleaning, and junk removal from the Euclid Avenue corridor up to San Antonio Heights.",
    highlights: [
      "Whole-home deep cleaning",
      "Office & medical suite cleaning",
      "Post-construction and remodel cleanup",
      "20% off for returning customers",
    ],
    content: [
      "Drive Euclid Avenue and you'll see the range of homes we clean in Upland: stately older properties near the median's horse trail, mid-century blocks on either side of Foothill, and the larger lots climbing toward San Antonio Heights. Each one gets a walkthrough first so the estimate matches the actual house, not a guess.",
      "Upland's commercial core keeps us busy too. We service dental and medical suites near San Antonio Regional Hospital, professional offices around Historic Downtown Upland, and retail along Mountain Avenue. Evening and early-morning slots keep us out of your business hours.",
      "Remodeling? Post-construction cleaning is one of our specialties — fine dust off every ledge, windows and tracks cleared, floors finished properly. Call (909) 443-9863 to schedule EMD Cleaning Service in Upland.",
    ],
    cities: [
      { name: "Historic Downtown Upland", lat: 34.0975, lng: -117.6484 },
      { name: "San Antonio Heights", lat: 34.15, lng: -117.656 },
      { name: "Euclid Corridor", lat: 34.11, lng: -117.65 },
      { name: "Magnolia", lat: 34.085, lng: -117.66 },
    ],
  },
  {
    slug: "rancho-cucamonga",
    name: "Rancho Cucamonga",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.1064,
    lng: -117.5931,
    description:
      "Professional cleaning services in Rancho Cucamonga, CA. EMD Cleaning Service covers Alta Loma, Etiwanda, Terra Vista, and the Victoria Gardens area with deep cleaning, regular cleaning, and junk removal.",
    highlights: [
      "Serving Alta Loma, Etiwanda & Terra Vista",
      "Recurring plans with the same crew",
      "Retail & office cleaning",
      "Free written estimates",
    ],
    content: [
      "Rancho Cucamonga spreads out, and the housing changes as you go north. Terra Vista condos, Etiwanda's newer subdivisions, and the older custom homes up in Alta Loma near the foothills all ask for something a little different — and Santa Ana wind season leaves grit on everything regardless of ZIP code.",
      "Retail and office clients around Victoria Gardens and the Foothill Boulevard corridor book us for maintenance cleaning that keeps their space presentable without interrupting customers. We also handle industrial-adjacent offices near the 15 and Fourth Street on early-morning schedules.",
      "Families here often pair a first deep clean with bi-weekly regular service, then add junk removal when the garage finally wins. Whatever the combination, call EMD Cleaning Service at (909) 443-9863 for a free Rancho Cucamonga estimate.",
    ],
    cities: [
      { name: "Victoria Gardens", lat: 34.109, lng: -117.536 },
      { name: "Alta Loma", lat: 34.13, lng: -117.593 },
      { name: "Etiwanda", lat: 34.133, lng: -117.515 },
      { name: "Terra Vista", lat: 34.106, lng: -117.559 },
    ],
  },
  {
    slug: "fontana",
    name: "Fontana",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0922,
    lng: -117.435,
    description:
      "Fontana, CA cleaning services for homes, rentals, and businesses. Deep cleaning, regular cleaning, maintenance cleaning, and junk removal from EMD Cleaning Service — six days a week.",
    highlights: [
      "House, apartment & rental cleaning",
      "Warehouse office cleaning",
      "Junk removal and clear-outs",
      "Monday–Saturday availability",
    ],
    content: [
      "Fontana runs from the older neighborhoods around Sierra Avenue and downtown all the way up to the newer tracts in North Fontana and Sierra Lakes. We cover the whole city, and our crews are used to the mix — a 1950s Kaiser-era home and a 2019 build have very different cleaning needs.",
      "With the 10, the 15, and the 210 all cutting through, Fontana is logistics country. We clean the office side of warehouses and distribution facilities along Valley Boulevard and Slover Avenue, plus break rooms and restrooms that see heavy daily use and need a real maintenance schedule rather than an occasional wipe-down.",
      "Moving in or out of Southridge? Combine a move-out deep clean with junk removal and hand over an empty, spotless unit in one appointment. Call (909) 443-9863 to book EMD Cleaning Service in Fontana.",
    ],
    cities: [
      { name: "North Fontana", lat: 34.14, lng: -117.44 },
      { name: "Sierra Lakes", lat: 34.142, lng: -117.47 },
      { name: "Downtown Fontana", lat: 34.0922, lng: -117.435 },
      { name: "Southridge", lat: 34.05, lng: -117.44 },
    ],
  },
  {
    slug: "san-bernardino",
    name: "San Bernardino",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.1083,
    lng: -117.2898,
    description:
      "Cleaning services in San Bernardino, CA. EMD Cleaning Service offers residential deep cleaning, recurring house cleaning, commercial maintenance cleaning, and junk removal across the city.",
    highlights: [
      "Residential & commercial service",
      "Deep cleans and one-time resets",
      "Office and storefront cleaning",
      "Honest, up-front pricing",
    ],
    content: [
      "San Bernardino is a big, varied city and we treat it that way. Our teams work the older bungalows near downtown and the courthouse district, the hillside homes up toward Del Rosa and the foothills, and the newer developments out in Verdemont on the far side of the 215.",
      "Commercial clients here include small offices, clinics, churches, and storefronts along E Street and Highland Avenue. Recurring maintenance cleaning is usually the right fit — a predictable schedule, the same crew, and restrooms and common areas that never fall behind.",
      "Whether it's a single deep clean to get ahead of things or a standing weekly visit, we'll walk the space and quote it honestly. Call EMD Cleaning Service at (909) 443-9863 for a free San Bernardino estimate.",
    ],
    cities: [
      { name: "Downtown San Bernardino", lat: 34.1083, lng: -117.2898 },
      { name: "Del Rosa", lat: 34.15, lng: -117.25 },
      { name: "Verdemont", lat: 34.19, lng: -117.42 },
      { name: "Muscoy", lat: 34.154, lng: -117.348 },
    ],
  },
  {
    slug: "east-valley",
    name: "East Valley",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0556,
    lng: -117.1825,
    description:
      "EMD Cleaning Service covers the East San Bernardino Valley — Redlands, Highland, Loma Linda, and Yucaipa — with deep cleaning, regular house cleaning, post-construction cleaning, and junk removal.",
    highlights: [
      "Redlands, Highland, Loma Linda & Yucaipa",
      "Historic home cleaning experience",
      "Post-construction and remodel cleanup",
      "20% off for returning customers",
    ],
    content: [
      "The East Valley has its own character — orange groves that never fully disappeared, Redlands' Victorian mansions along Olive Avenue, the university and medical campuses in Loma Linda, and the climb toward the mountains past Yucaipa. It's a quieter stretch of the county and the homes here often have age and detail worth protecting.",
      "We deep clean older properties with the care they need, handle recurring service for busy medical and university households near Loma Linda, and cover the newer subdivisions in Highland and East Highlands Ranch. Post-construction cleanup is common out here too, with steady remodeling activity across the older housing stock.",
      "Downtown Redlands businesses along State Street use us for after-hours maintenance cleaning that's done before the doors open. Call EMD Cleaning Service at (909) 443-9863 to schedule anywhere in the East Valley.",
    ],
    cities: [
      { name: "Redlands", lat: 34.0556, lng: -117.1825 },
      { name: "Highland", lat: 34.1283, lng: -117.2086 },
      { name: "Loma Linda", lat: 34.0483, lng: -117.2612 },
      { name: "Yucaipa", lat: 34.0336, lng: -117.0431 },
    ],
  },
  {
    slug: "jurupa-valley",
    name: "Jurupa Valley",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9971,
    lng: -117.4854,
    description:
      "Home and business cleaning in Jurupa Valley, CA. EMD Cleaning Service serves Mira Loma, Rubidoux, Glen Avon, and Pedley with deep cleaning, regular cleaning, and junk removal.",
    highlights: [
      "Serving Mira Loma, Rubidoux & Glen Avon",
      "Deep cleaning for dusty properties",
      "Junk removal and property clear-outs",
      "Free estimates, flexible scheduling",
    ],
    content: [
      "Jurupa Valley is spread across several older communities that only became one city in 2011, and each still feels distinct. We clean ranch properties with acreage in Rubidoux, tract homes in Glen Avon and Pedley, and the newer housing that filled in around Mira Loma as the warehouses arrived.",
      "Dust is the recurring theme here — rural lots, unpaved shoulders, and heavy truck traffic along the 60 all contribute. Our deep cleans focus on the places it settles: ceiling fans, blinds, window tracks, vents, and the tops of cabinets that rarely get touched.",
      "We also take on garage and yard clear-outs through our junk removal service, often alongside a move-out clean. Call (909) 443-9863 and EMD Cleaning Service will get Jurupa Valley scheduled.",
    ],
    cities: [
      { name: "Mira Loma", lat: 33.993, lng: -117.515 },
      { name: "Rubidoux", lat: 34.0, lng: -117.403 },
      { name: "Glen Avon", lat: 34.006, lng: -117.472 },
      { name: "Pedley", lat: 33.98, lng: -117.475 },
    ],
  },
  {
    slug: "corona",
    name: "Corona",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.8753,
    lng: -117.5664,
    description:
      "Corona, CA cleaning services from EMD Cleaning Service. Deep cleaning, regular house cleaning, maintenance cleaning, and post-construction cleanup for homes and offices near the 15 and the 91.",
    highlights: [
      "Residential deep & regular cleaning",
      "Office and suite cleaning",
      "Post-construction cleanup",
      "Same crew on recurring visits",
    ],
    content: [
      "Corona commuters have long days, and coming home to a clean house is worth something. We work across the city — the established neighborhoods inside the Grand Boulevard circle, the newer hillside developments in South Corona near Skyline Drive, and the family homes on the Eastvale and Norco edges.",
      "Business parks along the 15 corridor and the Corona Hills area keep us busy with office suites, showrooms, and light-industrial front offices. We schedule around your operating hours and build a maintenance plan that covers restrooms, break rooms, and floors on a set rotation.",
      "New construction and remodels around Corona get our post-construction treatment: dust removed from every surface, fixtures polished, floors done last. Call EMD Cleaning Service at (909) 443-9863 for a free Corona estimate.",
    ],
    cities: [
      { name: "Downtown Corona", lat: 33.8753, lng: -117.5664 },
      { name: "South Corona", lat: 33.82, lng: -117.54 },
      { name: "Eastvale", lat: 33.96, lng: -117.58 },
      { name: "Norco", lat: 33.931, lng: -117.549 },
    ],
  },
  {
    slug: "riverside",
    name: "Riverside",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9806,
    lng: -117.3755,
    description:
      "Riverside, CA house cleaning and commercial cleaning. EMD Cleaning Service provides deep cleaning, regular cleaning, specific area cleaning, and junk removal from downtown to La Sierra.",
    highlights: [
      "Downtown, Canyon Crest, Arlington & La Sierra",
      "Historic home and craftsman cleaning",
      "Student and rental turnovers",
      "20% off for returning customers",
    ],
    content: [
      "Downtown Riverside around the Mission Inn is full of buildings with real architectural detail, and the surrounding neighborhoods follow suit — Victorians, Spanish revivals, and Craftsman homes with original woodwork and tile. We match our products to those surfaces instead of running the same routine everywhere.",
      "Out from the center, the city changes quickly. We clean faculty and family homes in Canyon Crest near UCR, established blocks in Arlington and Magnolia Center, and the larger properties out in La Sierra and Woodcrest. Rental turnovers near the university are a regular part of our summer schedule.",
      "Small offices, studios, and shops downtown book recurring maintenance cleaning so their space looks the same on a Friday as it did Monday morning. Call (909) 443-9863 for a free Riverside estimate from EMD Cleaning Service.",
    ],
    cities: [
      { name: "Downtown Riverside", lat: 33.9806, lng: -117.3755 },
      { name: "Canyon Crest", lat: 33.96, lng: -117.33 },
      { name: "Arlington", lat: 33.925, lng: -117.43 },
      { name: "La Sierra", lat: 33.91, lng: -117.48 },
    ],
  },
  {
    slug: "san-dimas",
    name: "San Dimas",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.1067,
    lng: -117.8067,
    description:
      "House cleaning and commercial cleaning in San Dimas, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, move-in/move-out cleaning, and junk removal across San Dimas and the eastern San Gabriel Valley.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "San Dimas blends its Old West downtown with quiet foothill neighborhoods tucked against the San Gabriel Mountains. We clean historic homes near Bonita Avenue, newer builds around Via Verde, and equestrian properties out toward Horsethief Canyon — each with its own dust and debris challenges from the surrounding hillsides.",
      "On the commercial side, we service offices and retail near the 210/57 interchange and the light-industrial units along Arrow Highway. Post-construction cleanup is common here too, with steady development on the west side of town.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your San Dimas cleaning.",
    ],
    cities: [
      { name: "Via Verde", lat: 34.116, lng: -117.822 },
      { name: "Old Town San Dimas", lat: 34.1067, lng: -117.8067 },
      { name: "Horsethief Canyon", lat: 34.13, lng: -117.79 },
      { name: "San Dimas Canyon", lat: 34.128, lng: -117.803 },
    ],
  },
  {
    slug: "la-verne",
    name: "La Verne",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.1008,
    lng: -117.7678,
    description:
      "House cleaning and commercial cleaning in La Verne, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, specific area cleaning, and junk removal throughout La Verne.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "La Verne's tree-lined streets around the University of La Verne and the historic Old Town district call for a careful touch — we work around original hardwood floors, built-ins, and the kind of landscaping dust that comes with mature oak trees.",
      "We also clean student rentals near campus, office suites along Foothill Boulevard, and homes in the newer Marshall Canyon area. Move-out cleans are a frequent request from the university crowd.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your La Verne cleaning.",
    ],
    cities: [
      { name: "Old Town La Verne", lat: 34.1008, lng: -117.7678 },
      { name: "University District", lat: 34.108, lng: -117.762 },
      { name: "Marshall Canyon", lat: 34.13, lng: -117.75 },
      { name: "Wheeler Ridge", lat: 34.115, lng: -117.775 },
    ],
  },
  {
    slug: "covina",
    name: "Covina",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.09,
    lng: -117.8903,
    description:
      "House cleaning and commercial cleaning in Covina, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, junk removal, and move-in/move-out cleaning across Covina.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "Covina's mix of 1950s tract homes, Charter Oak-area properties, and the revitalized downtown around Citrus Avenue keeps our crews busy with everything from routine dusting to full deep cleans after a remodel.",
      "We also serve small businesses and medical offices near Covina's downtown corridor and along Azusa Avenue, plus post-construction cleanup for the steady stream of home renovations in the older neighborhoods.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Covina cleaning.",
    ],
    cities: [
      { name: "Downtown Covina", lat: 34.09, lng: -117.8903 },
      { name: "Charter Oak", lat: 34.098, lng: -117.85 },
      { name: "Covina Hills", lat: 34.105, lng: -117.87 },
      { name: "San Bernardino Road Corridor", lat: 34.095, lng: -117.88 },
    ],
  },
  {
    slug: "walnut",
    name: "Walnut",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0206,
    lng: -117.8653,
    description:
      "House cleaning and commercial cleaning in Walnut, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout Walnut.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "Walnut's hillside neighborhoods near Mt. San Antonio College and the quiet cul-de-sacs around Suzanne Ridge call for cleaning that respects tile, stone counters, and larger multi-story floor plans.",
      "We also clean homes near Walnut Ranch Park and the properties bordering Snow Creek Golf Course, along with the retail centers around Grand Avenue.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Walnut cleaning.",
    ],
    cities: [
      { name: "Suzanne Ridge", lat: 34.015, lng: -117.86 },
      { name: "Mt. SAC Area", lat: 34.048, lng: -117.883 },
      { name: "Walnut Ranch", lat: 34.022, lng: -117.855 },
      { name: "Snow Creek", lat: 34.01, lng: -117.845 },
    ],
  },
  {
    slug: "diamond-bar",
    name: "Diamond Bar",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0286,
    lng: -117.8103,
    description:
      "House cleaning and commercial cleaning in Diamond Bar, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, move-in/move-out cleaning, and junk removal across Diamond Bar.",
    highlights: [
      "Deep & recurring house cleaning",
      "Junk removal and haul-away",
      "Move-in / move-out cleans",
      "Free estimates, no obligation",
    ],
    content: [
      "Diamond Bar's hillside estates near The Country Estates and the family neighborhoods around Diamond Bar Boulevard both call for a thorough approach — high ceilings, large windows, and multi-level layouts are the norm here.",
      "We also serve the office parks near the 57/60 interchange and retail centers around Grand Avenue, with post-construction cleanup for the newer builds going up on the town's eastern edge.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Diamond Bar cleaning.",
    ],
    cities: [
      { name: "The Country Estates", lat: 34.005, lng: -117.79 },
      { name: "Diamond Bar Boulevard Corridor", lat: 34.0286, lng: -117.8103 },
      { name: "Steep Canyon", lat: 34.0, lng: -117.82 },
      { name: "Pantera", lat: 34.032, lng: -117.83 },
    ],
  },
  {
    slug: "rialto",
    name: "Rialto",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.1064,
    lng: -117.3703,
    description: "House cleaning and commercial cleaning in Rialto, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, specific area cleaning, and junk removal throughout Rialto.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Rialto's older neighborhoods near Riverside Avenue and the newer developments around Renaissance Parkway both keep our crews busy — from routine maintenance cleans to full deep cleans in homes near the warehouses and rail yards that generate extra dust.",
      "We also clean commercial and light-industrial spaces near the I-210/Riverside Ave corridor, and offer post-construction cleanup for the ongoing development in the Rialto business park.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Rialto cleaning.",
    ],
    cities: [
      { name: "Downtown Rialto", lat: 34.1064, lng: -117.3703 },
      { name: "Renaissance Parkway", lat: 34.12, lng: -117.39 },
      { name: "Bloomington Border", lat: 34.06, lng: -117.395 },
      { name: "Rialto Airport Area", lat: 34.09, lng: -117.41 },
    ],
  },
  {
    slug: "colton",
    name: "Colton",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0739,
    lng: -117.3134,
    description: "House cleaning and commercial cleaning in Colton, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, junk removal, and move-in/move-out cleaning across Colton.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Colton sits at the crossroads of two major rail lines, and homes near the historic downtown and the Grand Terrace border often need a deeper touch to handle the extra dust that comes with the territory.",
      "We also serve small businesses along Mount Vernon Avenue and light-industrial clients near the rail yards, with post-construction cleanup available for renovation projects throughout town.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Colton cleaning.",
    ],
    cities: [
      { name: "Downtown Colton", lat: 34.0739, lng: -117.3134 },
      { name: "Grand Terrace Border", lat: 34.03, lng: -117.31 },
      { name: "Agua Mansa", lat: 34.06, lng: -117.33 },
      { name: "Colton Business Park", lat: 34.08, lng: -117.32 },
    ],
  },
  {
    slug: "bloomington",
    name: "Bloomington",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.0561,
    lng: -117.3956,
    description: "House cleaning and commercial cleaning in Bloomington, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout Bloomington.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Bloomington's ranch-style homes and larger lots — many with horse property or extra outbuildings — call for cleaning crews who don't mind a bit more square footage or an extra shed to clear out.",
      "We also clean properties near the Bloomington Ave corridor and handle junk removal for the area's larger lots, a frequent request given how much storage space Bloomington homes tend to have.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Bloomington cleaning.",
    ],
    cities: [
      { name: "Bloomington Avenue Corridor", lat: 34.0561, lng: -117.3956 },
      { name: "Cedar Avenue", lat: 34.06, lng: -117.4 },
      { name: "Locust Avenue", lat: 34.05, lng: -117.39 },
      { name: "Santa Ana Avenue", lat: 34.045, lng: -117.385 },
    ],
  },
  {
    slug: "norco",
    name: "Norco",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9312,
    lng: -117.5486,
    description: "House cleaning and commercial cleaning in Norco, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, junk removal, and move-in/move-out cleaning across Norco.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Norco is horse country, and we know it — dusty tack rooms, hay dust tracked indoors, and large ranch-style homes on acreage are all in a day's work for our crews.",
      "We also clean homes near the Santa Ana River trails and the newer developments off Hidden Valley Parkway, plus small businesses along Sixth Street.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Norco cleaning.",
    ],
    cities: [
      { name: "Horsetown Norco", lat: 33.9312, lng: -117.5486 },
      { name: "Hidden Valley", lat: 33.94, lng: -117.53 },
      { name: "River Road", lat: 33.92, lng: -117.56 },
      { name: "Norco Hills", lat: 33.925, lng: -117.545 },
    ],
  },
  {
    slug: "north-pomona",
    name: "North Pomona",
    state: "CA",
    region: "Los Angeles County",
    isPrimary: false,
    lat: 34.0722,
    lng: -117.7509,
    description: "House cleaning and commercial cleaning in North Pomona, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout the North Pomona area.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "North Pomona's neighborhoods near the Fairplex and the homes bordering La Verne and Claremont keep our crews busy with everything from routine dusting to deep cleans timed around fair season traffic and dust.",
      "We also serve the office and retail spaces near Foothill Boulevard, plus post-construction cleanup for the steady renovation work in the area's older housing stock.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your North Pomona cleaning.",
    ],
    cities: [
      { name: "Fairplex Area", lat: 34.0722, lng: -117.7509 },
      { name: "Foothill Corridor", lat: 34.08, lng: -117.75 },
      { name: "Ganesha Hills", lat: 34.07, lng: -117.735 },
      { name: "North Pomona Business Park", lat: 34.065, lng: -117.76 },
    ],
  },
  {
    slug: "alta-loma",
    name: "Alta Loma",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.1256,
    lng: -117.5972,
    description: "House cleaning and commercial cleaning in Alta Loma, Rancho Cucamonga, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, and junk removal throughout Alta Loma.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Alta Loma's foothill homes near Red Hill Country Club and the neighborhoods along Carnelian Street often deal with extra dust blowing down from the San Gabriel foothills — our deep cleans are built for it.",
      "We also clean properties near Alta Loma Park and the retail centers along Baseline Road, with move-in/move-out cleans a regular request in this family-heavy part of Rancho Cucamonga.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Alta Loma cleaning.",
    ],
    cities: [
      { name: "Red Hill", lat: 34.115, lng: -117.605 },
      { name: "Carnelian Corridor", lat: 34.13, lng: -117.598 },
      { name: "Alta Loma Park", lat: 34.128, lng: -117.59 },
      { name: "Hermosa Estates", lat: 34.132, lng: -117.585 },
    ],
  },
  {
    slug: "north-cucamonga",
    name: "North Cucamonga",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.1197,
    lng: -117.5931,
    description: "House cleaning and commercial cleaning in North Cucamonga, Rancho Cucamonga, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout North Cucamonga.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "North Cucamonga's historic Route 66 corridor and the hillside neighborhoods above Baseline Road both call for careful cleaning — older homes with original features next to newer hillside estates with big windows and lots of glass.",
      "We also serve the wineries-turned-residential pockets near Old Cucamonga and the retail strips along Foothill Boulevard, plus post-construction cleanup for ongoing infill development.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your North Cucamonga cleaning.",
    ],
    cities: [
      { name: "Old Cucamonga", lat: 34.1197, lng: -117.5931 },
      { name: "Route 66 Corridor", lat: 34.115, lng: -117.59 },
      { name: "North Hills", lat: 34.128, lng: -117.6 },
      { name: "Deer Canyon", lat: 34.14, lng: -117.605 },
    ],
  },
  {
    slug: "etiwanda",
    name: "Etiwanda",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 34.135,
    lng: -117.5211,
    description: "House cleaning and commercial cleaning in Etiwanda, Rancho Cucamonga, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, and junk removal throughout Etiwanda.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Etiwanda's newer master-planned communities near Victoria Gardens and the older homes along the historic Eucalyptus windrows both keep our crews busy — from full deep cleans in larger new-construction homes to routine maintenance in established neighborhoods.",
      "We also clean properties near East Avenue and the retail corridor around Foothill Boulevard, with post-construction cleanup available for the area's continued growth.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Etiwanda cleaning.",
    ],
    cities: [
      { name: "Victoria Gardens Area", lat: 34.13, lng: -117.525 },
      { name: "Eucalyptus Windrows", lat: 34.14, lng: -117.515 },
      { name: "East Etiwanda", lat: 34.145, lng: -117.505 },
      { name: "Henderson Creek", lat: 34.15, lng: -117.5 },
    ],
  },
  {
    slug: "ontario-ranch",
    name: "Ontario Ranch",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 33.995,
    lng: -117.61,
    description: "House cleaning and commercial cleaning in Ontario Ranch, Ontario, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, move-in/move-out cleaning, and junk removal throughout Ontario Ranch.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Ontario Ranch is one of the fastest-growing pockets in the Inland Empire, and our crews are right there with it — new-construction homes, fresh paint smell and all, are a regular first-clean request before move-in.",
      "We also serve the retail centers around New Haven and the office parks near Ontario Ranch Road, with post-construction cleanup a steady part of the work given how much building is happening south of Riverside Drive.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Ontario Ranch cleaning.",
    ],
    cities: [
      { name: "New Haven", lat: 33.99, lng: -117.6 },
      { name: "The Preserve at Ontario Ranch", lat: 33.985, lng: -117.615 },
      { name: "Park Place", lat: 34.0, lng: -117.605 },
      { name: "Water Front", lat: 33.995, lng: -117.62 },
    ],
  },
  {
    slug: "rubidoux",
    name: "Rubidoux",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9975,
    lng: -117.4192,
    description: "House cleaning and commercial cleaning in Rubidoux, Jurupa Valley, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, and junk removal throughout Rubidoux.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Rubidoux's hillside homes near Mt. Rubidoux and the neighborhoods along Mission Boulevard call for cleaning that keeps up with the dust that rolls in off the Santa Ana riverbed.",
      "We also clean properties near the Rubidoux business district and handle junk removal for the area's mix of older single-family homes and larger lots.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Rubidoux cleaning.",
    ],
    cities: [
      { name: "Mt. Rubidoux", lat: 33.9975, lng: -117.4192 },
      { name: "Mission Boulevard Corridor", lat: 34.0, lng: -117.41 },
      { name: "Riverview", lat: 33.99, lng: -117.42 },
      { name: "Santa Ana River Trail", lat: 33.985, lng: -117.43 },
    ],
  },
  {
    slug: "mira-loma",
    name: "Mira Loma",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9928,
    lng: -117.5011,
    description: "House cleaning and commercial cleaning in Mira Loma, Jurupa Valley, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout Mira Loma.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "Mira Loma sits in the middle of one of the busiest warehouse corridors in the Inland Empire, and homes nearby often need extra help keeping ahead of truck dust and diesel grime.",
      "We also clean properties near Wineville and the equestrian pockets that still dot Mira Loma's older neighborhoods, plus offer junk removal for the area's larger residential lots.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Mira Loma cleaning.",
    ],
    cities: [
      { name: "Wineville", lat: 34.0, lng: -117.51 },
      { name: "Mira Loma Business Corridor", lat: 33.99, lng: -117.495 },
      { name: "Sky Country", lat: 33.985, lng: -117.505 },
      { name: "Riverside Drive Corridor", lat: 33.995, lng: -117.485 },
    ],
  },
  {
    slug: "la-sierra",
    name: "La Sierra",
    state: "CA",
    region: "Riverside County",
    isPrimary: false,
    lat: 33.9075,
    lng: -117.4728,
    description: "House cleaning and commercial cleaning in La Sierra, Riverside, CA. EMD Cleaning Service provides deep cleans, recurring maintenance, junk removal, and move-in/move-out cleaning across La Sierra.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "La Sierra's neighborhoods near La Sierra University and the hillside streets around Two Rivers Plaza call for cleaning crews comfortable with both student rentals and larger family homes.",
      "We also serve retail and office space near the La Sierra Metrolink corridor, with move-out cleans a common request from the university-area rental market.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your La Sierra cleaning.",
    ],
    cities: [
      { name: "La Sierra University Area", lat: 33.9075, lng: -117.4728 },
      { name: "Two Rivers Plaza", lat: 33.9, lng: -117.46 },
      { name: "Arlanza Border", lat: 33.925, lng: -117.44 },
      { name: "La Sierra Hills", lat: 33.895, lng: -117.48 },
    ],
  },
  {
    slug: "butterfield",
    name: "Butterfield",
    state: "CA",
    region: "San Bernardino County",
    isPrimary: false,
    lat: 33.966,
    lng: -117.706,
    description: "House cleaning and commercial cleaning in the Butterfield area of Chino Hills, CA. EMD Cleaning Service handles deep cleans, recurring maintenance cleaning, and junk removal throughout Butterfield.",
    highlights: ["Deep & recurring house cleaning", "Junk removal and haul-away", "Move-in / move-out cleans", "Free estimates, no obligation"],
    content: [
      "The Butterfield and Butterfield Ranch neighborhoods in southern Chino Hills are full of two-story homes from the late '80s and '90s, with the kind of vaulted ceilings and large windows that make a real difference when they're professionally cleaned.",
      "We also clean properties near Chino Hills Dog Park and Meadow Park Trail, and handle move-in/move-out cleans for the steady turnover in this family-friendly pocket near the 71 Freeway.",
      "Ask about maintenance cleaning if you'd rather never think about it again. Call EMD Cleaning Service at (909) 443-9863 to schedule your Butterfield cleaning.",
    ],
    cities: [
      { name: "Butterfield Ranch", lat: 33.966, lng: -117.706 },
      { name: "South Trails", lat: 33.96, lng: -117.71 },
      { name: "The Meadows", lat: 33.97, lng: -117.7 },
      { name: "Los Serranos Border", lat: 33.975, lng: -117.715 },
    ],
  },
];

export const primaryLocation = locations.find((l) => l.isPrimary) ?? locations[0];
